import { useState } from "react";
import { useNotify, useRecordContext, useRefresh } from "ra-core";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/admin/confirm";

import { skipTraceOne } from "./skipTrace";
import type { Prospect } from "../types";

const MESSAGES: Record<string, { text: string; type: "success" | "info" | "warning" }> = {
  traced: { text: "Skip traced — phones added", type: "success" },
  queued: { text: "Skip trace queued at the source", type: "info" },
  noMatch: { text: "No skip-trace match found", type: "info" },
  skipped: { text: "Needs an owner name + address to skip trace", type: "warning" },
  failed: { text: "Skip trace failed", type: "warning" },
};

/**
 * Single-prospect skip trace, shown on the prospect card next to
 * "Refresh from source". Uses the same routing as the bulk action.
 * A prospect that was already traced is never re-traced without the
 * user explicitly authorizing it in the confirm dialog.
 */
export const ProspectSkipTraceButton = () => {
  const record = useRecordContext<Prospect>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!record) return null;

  const run = async () => {
    setLoading(true);
    try {
      const outcome = await skipTraceOne(record);
      const m = MESSAGES[outcome] ?? MESSAGES.failed;
      notify(m.text, { type: m.type });
      // Refresh on any outcome that stamps skip_traced_at.
      if (outcome === "traced" || outcome === "queued" || outcome === "noMatch")
        refresh();
    } finally {
      setLoading(false);
    }
  };

  const handle = () => {
    if (record.skip_traced_at) {
      setConfirmOpen(true); // already traced — ask for authorization first
      return;
    }
    void run();
  };

  const tracedOn = record.skip_traced_at
    ? new Date(record.skip_traced_at).toLocaleDateString()
    : null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handle}
        disabled={loading}
        title={
          tracedOn
            ? `Already skip traced on ${tracedOn} — click to re-trace`
            : "Find owner phone numbers (Endato skip trace)"
        }
      >
        <Search className={`size-4 ${loading ? "animate-pulse" : ""}`} />
        {loading ? "Skip tracing…" : tracedOn ? "Re-trace" : "Skip trace"}
      </Button>
      <Confirm
        isOpen={confirmOpen}
        title="Re-run skip trace?"
        content={`This prospect was already skip traced on ${tracedOn}. Running it again may incur another lookup. Re-trace anyway?`}
        confirm="Re-trace"
        cancel="Cancel"
        onConfirm={() => {
          setConfirmOpen(false);
          void run();
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};
